// src/services/opsViewsService.ts
//
// Supabase access for the three standalone ops list views that outlived the
// pre-pivot stack: /kb-updates, /health and /prospects. Story 4.10 deleted
// resolutionsService along with the Morning Queue and Escalations it served;
// these four methods are the part that still has a screen, moved here verbatim
// so the survivors don't keep a retired module alive.
//
// Components/hooks consume the clean `src/types/yak.ts` types only; results are
// cast once per method at this boundary.

import { supabase } from '../lib/supabase'
import type {
  YakCustomerHealth,
  YakKbUpdate,
  YakProspect,
  YakResolution,
} from '../types/yak'

class OpsViewsService {
  // KB updates are read-only history now: kb-update-intake (the updates@ inbound
  // feeder) retired with Story 4.10, so no new rows arrive. Epic 2's /kb is the
  // live KB workflow.
  async listKbUpdates(): Promise<YakKbUpdate[]> {
    const { data, error } = await supabase
      .from('yak_kb_updates')
      .select('*')
      .order('received_at', { ascending: false })

    if (error) throw new Error(error.message)
    return (data as unknown as YakKbUpdate[] | null) || []
  }

  // Mark a KB update processed — i.e. after the requested content has been
  // uploaded through the console (/kb → kb-ingest → yak_kb_chunks), which is
  // the only live KB path. This flag is bookkeeping on the request, not an
  // ingestion step: it publishes nothing on its own. (It used to say "after
  // manually running yak:ingest"; that script writes the orphaned legacy
  // yak_documents corpus and is retired — Story 4.10 code review.)
  async markKbUpdateComplete(id: string): Promise<void> {
    const { error } = await supabase
      .from('yak_kb_updates')
      .update({
        status: 'complete',
        completed_at: new Date().toISOString(),
      })
      .eq('id', id)

    if (error) throw new Error(error.message)
  }

  // ScriptYak-internal sales pipeline (super_admin only via RLS). Still fed
  // live by the cal-booking webhook.
  async listProspects(): Promise<YakProspect[]> {
    const { data, error } = await supabase
      .from('yak_prospects')
      .select('*')
      .order('next_action_date', { ascending: true })

    if (error) throw new Error(error.message)
    return (data as unknown as YakProspect[] | null) || []
  }

  // Per-organization Yak health: total interactions, auto-resolution
  // rate (route_label='auto' / total) and avg response time
  // (resolved_at - received_at). RLS scopes resolutions to the caller's
  // org for admins; super_admin sees all.
  //
  // Reads yak_resolutions, which is FROZEN (no writer since Story 4.10) and is
  // covered by database/archive/DEFERRED_drop_legacy_agent_stack.sql — running
  // that script retires this screen with it. See deferred-work.md.
  async customerHealth(): Promise<YakCustomerHealth[]> {
    const { data: orgs, error: orgError } = await supabase
      .from('organizations')
      .select('id, name')

    if (orgError) throw new Error(orgError.message)

    const { data: resData, error: resError } = await supabase
      .from('yak_resolutions')
      .select('organization_id, route_label, received_at, resolved_at')

    if (resError) throw new Error(resError.message)

    const resolutions =
      (resData as Array<
        Pick<
          YakResolution,
          'organization_id' | 'route_label' | 'received_at' | 'resolved_at'
        >
      > | null) || []

    return ((orgs as Array<{ id: string; name: string }> | null) || []).map(
      org => {
        const orgResolutions = resolutions.filter(
          r => r.organization_id === org.id
        )
        const total = orgResolutions.length
        const autoResolved = orgResolutions.filter(
          r => r.route_label === 'auto'
        ).length

        const responseMsValues = orgResolutions
          .filter(r => r.received_at !== null && r.resolved_at !== null)
          .map(r => {
            const received = new Date(r.received_at as string).getTime()
            const resolved = new Date(r.resolved_at as string).getTime()
            return resolved - received
          })
          .filter(ms => Number.isFinite(ms) && ms >= 0)

        const avgResponseMs =
          responseMsValues.length > 0
            ? responseMsValues.reduce((sum, ms) => sum + ms, 0) /
              responseMsValues.length
            : null

        return {
          organization_id: org.id,
          organization_name: org.name,
          total,
          autoResolved,
          autoResolutionRate: total > 0 ? autoResolved / total : null,
          avgResponseMs,
        }
      }
    )
  }
}

export const opsViewsService = new OpsViewsService()
